import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { 
  Home, Video, Settings, LogOut, Users, Trophy, Plus, Edit2, Trash2, 
  Upload, ExternalLink, MessageCircle 
} from "lucide-react";
import { API } from "@/config";
import CsvImportTab from "@/components/admin/CsvImportTab";
import CoachEraModal from "@/components/admin/CoachEraModal";
import SeasonModal from "@/components/admin/SeasonModal";
import VideoModal from "@/components/admin/VideoModal";

const AdminDashboard = ({ token, onLogout }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [coachEras, setCoachEras] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [videos, setVideos] = useState([]);
  const [siteSettings, setSiteSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(null);
  const [editItem, setEditItem] = useState(null);
  
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  
  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount-only fetch
  
  const fetchData = async () => {
    try {
      const [statsRes, erasRes, seasonsRes, videosRes, settingsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, authHeaders),
        axios.get(`${API}/coach-eras`),
        axios.get(`${API}/seasons`),
        axios.get(`${API}/videos?limit=1000`),
        axios.get(`${API}/settings`)
      ]);
      setStats(statsRes.data);
      setCoachEras(erasRes.data);
      setSeasons(seasonsRes.data);
      setVideos(videosRes.data);
      setSiteSettings(settingsRes.data);
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleUpdateSettings = async () => {
    try {
      await axios.put(`${API}/admin/settings`, siteSettings, authHeaders);
      alert('Settings saved!');
    } catch (err) {
      alert('Error saving settings');
    }
  };
  
  const handleUpdateCoachEra = async (id, data) => {
    try {
      await axios.put(`${API}/admin/coach-eras/${id}`, data, authHeaders);
      setShowModal(null);
      setEditItem(null);
      fetchData();
    } catch (err) {
      alert('Error updating coach era');
    }
  };
  
  const handleCreateSeason = async (data) => {
    try {
      await axios.post(`${API}/admin/seasons`, data, authHeaders);
      setShowModal(null);
      fetchData();
    } catch (err) {
      alert('Error creating season');
    }
  };
  
  const handleUpdateSeason = async (id, data) => {
    try {
      await axios.put(`${API}/admin/seasons/${id}`, data, authHeaders);
      setShowModal(null);
      setEditItem(null);
      fetchData();
    } catch (err) {
      alert('Error updating season');
    }
  };
  
  const handleDeleteSeason = async (id) => {
    if (!window.confirm('Delete this season and all its videos?')) return;
    try {
      await axios.delete(`${API}/admin/seasons/${id}`, authHeaders);
      fetchData();
    } catch (err) {
      alert('Error deleting season');
    }
  };
  
  const handleCreateVideo = async (data) => {
    try {
      await axios.post(`${API}/admin/videos`, data, authHeaders);
      setShowModal(null);
      fetchData();
    } catch (err) {
      alert('Error creating video');
    }
  };
  
  const handleUpdateVideo = async (id, data) => {
    try {
      await axios.put(`${API}/admin/videos/${id}`, data, authHeaders);
      setShowModal(null);
      setEditItem(null);
      fetchData();
    } catch (err) {
      alert('Error updating video');
    }
  };
  
  const handleDeleteVideo = async (id) => {
    if (!window.confirm('Delete this video?')) return;
    try {
      await axios.delete(`${API}/admin/videos/${id}`, authHeaders);
      fetchData();
    } catch (err) {
      alert('Error deleting video');
    }
  };
  
  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }
  
  return (
    <div className="admin-layout">
      <aside className="admin-sidebar">
        <Link to="/" className="admin-sidebar-logo">
          Funchester <span style={{ color: 'var(--primary)' }}>TV</span>
        </Link>
        
        <nav className="admin-sidebar-nav">
          <button 
            className={`admin-sidebar-link ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
            data-testid="admin-nav-dashboard"
          >
            <Home size={18} />
            Dashboard
          </button>
          <button 
            className={`admin-sidebar-link ${activeTab === 'coach-eras' ? 'active' : ''}`}
            onClick={() => setActiveTab('coach-eras')}
            data-testid="admin-nav-coach-eras"
          >
            <Users size={18} />
            Coach Eras
          </button>
          <button 
            className={`admin-sidebar-link ${activeTab === 'seasons' ? 'active' : ''}`}
            onClick={() => setActiveTab('seasons')}
            data-testid="admin-nav-seasons"
          >
            <Trophy size={18} />
            Seasons
          </button>
          <button 
            className={`admin-sidebar-link ${activeTab === 'videos' ? 'active' : ''}`}
            onClick={() => setActiveTab('videos')}
            data-testid="admin-nav-videos"
          >
            <Video size={18} />
            Videos
          </button>
          <button 
            className={`admin-sidebar-link ${activeTab === 'csv-import' ? 'active' : ''}`}
            onClick={() => setActiveTab('csv-import')}
            data-testid="admin-nav-csv-import"
          >
            <Upload size={18} />
            CSV Import
          </button>
          <button 
            className={`admin-sidebar-link ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
            data-testid="admin-nav-settings"
          >
            <Settings size={18} />
            Stream Settings
          </button>
          <Link to="/" className="admin-sidebar-link">
            <ExternalLink size={18} />
            View Site
          </Link>
          <button 
            className="admin-sidebar-link"
            onClick={onLogout}
            data-testid="admin-logout-btn"
          >
            <LogOut size={18} />
            Logout
          </button>
        </nav>
      </aside>
      
      <main className="admin-content">
        {activeTab === 'dashboard' && (
          <>
            <div className="admin-header">
              <h1 className="admin-title">Dashboard</h1>
            </div>
            
            <div className="stats-grid">
              <div className="stat-card">
                <p className="stat-card-label">Total Videos</p>
                <p className="stat-card-value">{stats?.total_videos || 0}</p>
              </div>
              <div className="stat-card">
                <p className="stat-card-label">Total Seasons</p>
                <p className="stat-card-value">{stats?.total_seasons || 0}</p>
              </div>
              <div className="stat-card">
                <p className="stat-card-label">Games</p>
                <p className="stat-card-value">{stats?.total_games || 0}</p>
              </div>
              <div className="stat-card">
                <p className="stat-card-label">Documentaries</p>
                <p className="stat-card-value">{stats?.total_documentaries || 0}</p>
              </div>
            </div>
            
            <div className="stat-card" style={{ marginBottom: '2rem' }}>
              <p className="stat-card-label">Total Views</p>
              <p className="stat-card-value">{stats?.total_views?.toLocaleString() || 0}</p>
            </div>
          </>
        )}
        
        {activeTab === 'coach-eras' && (
          <>
            <div className="admin-header">
              <h1 className="admin-title">Coach Eras</h1>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                Update coach photos by clicking Edit
              </p>
            </div>
            
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Photo</th>
                    <th>Coach</th>
                    <th>Years</th>
                    <th>Championships</th>
                    <th>Seasons</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {coachEras.map((era) => (
                    <tr key={era.id}>
                      <td>
                        <img 
                          src={era.thumbnail || 'https://via.placeholder.com/40'} 
                          alt={era.coach_name}
                          style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }}
                        />
                      </td>
                      <td><strong>{era.coach_name}</strong></td>
                      <td>{era.years}</td>
                      <td>{era.championships}</td>
                      <td>{era.season_count}</td>
                      <td>
                        <button 
                          className="btn btn-secondary btn-sm"
                          onClick={() => { setEditItem(era); setShowModal('coach-era'); }}
                        >
                          <Edit2 size={14} /> Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        
        {activeTab === 'seasons' && (
          <>
            <div className="admin-header">
              <h1 className="admin-title">Seasons</h1>
              <button 
                className="btn btn-primary"
                onClick={() => { setEditItem(null); setShowModal('season'); }}
                data-testid="add-season-btn"
              >
                <Plus size={16} />
                Add Season
              </button>
            </div>
            
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Year</th>
                    <th>Title</th>
                    <th>Videos</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {seasons.map((season) => (
                    <tr key={season.id}>
                      <td><strong>{season.year}</strong></td>
                      <td>{season.title}</td>
                      <td>{season.video_count}</td>
                      <td>
                        <div className="table-actions">
                          <button 
                            className="btn btn-secondary btn-sm"
                            onClick={() => { setEditItem(season); setShowModal('season'); }}
                          >
                            <Edit2 size={14} />
                          </button>
                          <button 
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDeleteSeason(season.id)}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        
        {activeTab === 'videos' && (
          <>
            <div className="admin-header">
              <h1 className="admin-title">Videos</h1>
              <button 
                className="btn btn-primary"
                onClick={() => { setEditItem(null); setShowModal('video'); }}
                data-testid="add-video-btn"
              >
                <Plus size={16} />
                Add Video
              </button>
            </div>
            
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Season</th>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Views</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {videos.map((video) => (
                    <tr key={video.id}>
                      <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {video.title}
                      </td>
                      <td>{video.season_year}</td>
                      <td>{video.date}</td>
                      <td>
                        <span className="btn btn-secondary btn-sm" style={{ cursor: 'default', padding: '0.25rem 0.5rem' }}>
                          {video.video_type}
                        </span>
                      </td>
                      <td>{video.views}</td>
                      <td>
                        <div className="table-actions">
                          <button 
                            className="btn btn-secondary btn-sm"
                            onClick={() => { setEditItem(video); setShowModal('video'); }}
                          >
                            <Edit2 size={14} />
                          </button>
                          <button 
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDeleteVideo(video.id)}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        
        {activeTab === 'settings' && (
          <>
            <div className="admin-header">
              <h1 className="admin-title">Stream Settings</h1>
            </div>
            
            <div className="settings-card">
              <h3>Live Stream Configuration</h3>
              <p className="settings-help">Paste your stream URL here. When a stream goes down, just replace the URL with a new one.</p>
              
              <div className="form-group">
                <label className="form-label">Stream Title</label>
                <input
                  type="text"
                  className="form-input"
                  value={siteSettings.stream_title || ''}
                  onChange={(e) => setSiteSettings({ ...siteSettings, stream_title: e.target.value })}
                  placeholder="e.g., Kentucky vs Louisville"
                  data-testid="stream-title-input"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Stream URL</label>
                <input
                  type="text"
                  className="form-input"
                  value={siteSettings.stream_url || ''}
                  onChange={(e) => setSiteSettings({ ...siteSettings, stream_url: e.target.value })}
                  placeholder="Paste embed URL here (e.g., https://ok.ru/videoembed/xxx or any iframe URL)"
                  data-testid="stream-url-input"
                />
                <p className="form-help">Supports ok.ru, YouTube embeds, or any iframe-compatible stream URL</p>
              </div>
              
              <div className="form-group">
                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <input
                    type="checkbox"
                    checked={siteSettings.stream_active || false}
                    onChange={(e) => setSiteSettings({ ...siteSettings, stream_active: e.target.checked })}
                    style={{ width: 20, height: 20 }}
                    data-testid="stream-active-checkbox"
                  />
                  Stream is Active (show video player instead of placeholder)
                </label>
              </div>
              
              <button 
                className="btn btn-primary"
                onClick={handleUpdateSettings}
                data-testid="save-settings-btn"
              >
                Save Settings
              </button>
            </div>
            
            <div className="settings-card" style={{ marginTop: '2rem' }}>
              <h3><MessageCircle size={20} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '0.5rem' }} />Game Day Chat</h3>
              <p className="settings-help">Clear all chat messages after a game ends to start fresh for the next one.</p>
              <button 
                className="btn btn-danger"
                onClick={async () => {
                  if (!window.confirm('Clear ALL chat messages? This cannot be undone.')) return;
                  try {
                    const res = await axios.delete(`${API}/admin/chat/clear`, authHeaders);
                    alert(res.data.message);
                  } catch (err) {
                    alert('Error clearing chat');
                  }
                }}
                data-testid="clear-chat-btn"
              >
                <Trash2 size={16} />
                Clear Chat History
              </button>
            </div>
          </>
        )}
        
        {activeTab === 'csv-import' && (
          <CsvImportTab token={token} seasons={seasons} onImportComplete={fetchData} />
        )}
      </main>
      
      {showModal === 'coach-era' && (
        <CoachEraModal
          era={editItem}
          token={token}
          onClose={() => { setShowModal(null); setEditItem(null); }}
          onSave={(data) => handleUpdateCoachEra(editItem.id, data)}
        />
      )}
      
      {showModal === 'season' && (
        <SeasonModal
          season={editItem}
          coachEras={coachEras}
          onClose={() => { setShowModal(null); setEditItem(null); }}
          onSave={editItem ? (data) => handleUpdateSeason(editItem.id, data) : handleCreateSeason}
        />
      )}
      
      {showModal === 'video' && (
        <VideoModal
          video={editItem}
          seasons={seasons}
          token={token}
          onClose={() => { setShowModal(null); setEditItem(null); }}
          onSave={editItem ? (data) => handleUpdateVideo(editItem.id, data) : handleCreateVideo}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
